// Effects building blocks: generated reverb impulse response, ping-pong delay, noise
// buffer and a soft safety clipper curve.

export function makeNoiseBuffer(ctx: BaseAudioContext, seconds = 2): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

/**
 * A lush stereo hall: 22 ms pre-delay, a cluster of early reflections, then exponentially
 * decaying noise whose brightness falls over time (a one-pole lowpass whose cutoff sweeps
 * from ~7 kHz down to ~900 Hz), so the tail darkens the way real rooms do.
 */
export function makeReverbIR(ctx: BaseAudioContext, seconds = 4.5): AudioBuffer {
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * seconds);
  const buf = ctx.createBuffer(2, len, sr);
  const pre = Math.floor(0.022 * sr);
  const rt60 = seconds * 0.92;
  const tau = rt60 / 6.91; // amplitude time constant for -60 dB at rt60
  const fade = Math.floor(0.25 * sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    // Early reflections.
    let seed = ch * 7919 + 13;
    const rand = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    for (let k = 0; k < 14; k++) {
      const at = pre + Math.floor((0.004 + rand() * 0.075) * sr);
      if (at < len) d[at] = (d[at] ?? 0) + (rand() < 0.5 ? -1 : 1) * (0.35 + 0.45 * rand()) * (1 - k / 20);
    }
    // Diffuse tail.
    let lp = 0;
    const onset = Math.floor(0.012 * sr);
    for (let i = pre; i < len; i++) {
      const tt = (i - pre) / sr;
      const cutoff = 900 + 6100 * Math.exp(-tt / 0.9);
      const a = 1 - Math.exp((-2 * Math.PI * cutoff) / sr);
      lp += a * (Math.random() * 2 - 1 - lp);
      const rise = Math.min(1, (i - pre) / onset);
      let env = Math.exp(-tt / tau) * rise;
      if (i > len - fade) env *= (len - i) / fade;
      // Boost to compensate for the lowpass energy loss.
      d[i] = (d[i] ?? 0) + lp * env * 1.6;
    }
  }
  // Normalise energy so the convolver output level is predictable.
  let e = 0;
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) e += (d[i] ?? 0) * (d[i] ?? 0);
  }
  const norm = 1 / Math.sqrt(e / 2);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (d[i] ?? 0) * norm;
  }
  return buf;
}

export interface PingPong {
  input: GainNode;
  output: GainNode;
  nodes: AudioNode[];
}

/** Stereo ping-pong delay: L -> R -> L ..., lowpass and highpass in the feedback path. */
export function makePingPong(ctx: BaseAudioContext, time: number, feedback: number): PingPong {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const dl = ctx.createDelay(2);
  const dr = ctx.createDelay(2);
  dl.delayTime.value = time;
  dr.delayTime.value = time;
  const lpl = ctx.createBiquadFilter();
  lpl.type = "lowpass";
  lpl.frequency.value = 3200;
  const lpr = ctx.createBiquadFilter();
  lpr.type = "lowpass";
  lpr.frequency.value = 2800;
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 280;
  const fbl = ctx.createGain();
  fbl.gain.value = 1;
  const fbr = ctx.createGain();
  fbr.gain.value = feedback;
  const merger = ctx.createChannelMerger(2);

  input.connect(hp).connect(dl);
  dl.connect(lpl).connect(fbl).connect(dr);
  dr.connect(lpr).connect(fbr).connect(dl);
  // Each hop through L->R->L costs `feedback` once, so a repeat is ~feedback quieter per bounce pair;
  // compensate with a sqrt so each audible repeat is ~feedback relative to the previous one.
  fbl.gain.value = Math.sqrt(feedback);
  fbr.gain.value = Math.sqrt(feedback);
  lpl.connect(merger, 0, 0);
  lpr.connect(merger, 0, 1);
  merger.connect(output);
  return { input, output, nodes: [dl, dr, lpl, lpr, hp, fbl, fbr, merger] };
}

/**
 * Soft clipper curve. Input is expected pre-scaled by 0.5 (so the curve domain [-1, 1]
 * represents a signal in [-2, 2]). Linear up to 0.75, then a tanh knee that never
 * exceeds 0.97.
 */
export function makeSoftClipCurve(points = 4097): Float32Array<ArrayBuffer> {
  const c = new Float32Array(points);
  const knee = 0.75;
  const room = 0.97 - knee;
  for (let i = 0; i < points; i++) {
    const x = ((i / (points - 1)) * 2 - 1) * 2;
    const ax = Math.abs(x);
    const y = ax <= knee ? ax : knee + room * Math.tanh((ax - knee) / room);
    c[i] = Math.sign(x) * y;
  }
  return c;
}
